import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiCalendar, FiArrowRight } from "react-icons/fi";
import { SEO } from "@/components/seo";
import { CardSkeleton } from "@/components/common";

type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  featuredImage: string | null;
  publishedAt: string;
};

export function BlogPage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch("/api/blog/posts")
      .then(async (res) => {
        const data = await res.json() as Post[] | { data: Post[] };
        // Handle both array response and object with data property
        if (Array.isArray(data)) {
          setPosts(data);
        } else if (data && Array.isArray(data.data)) {
          setPosts(data.data);
        } else {
          console.error("Unexpected API response format:", data);
          setPosts([]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch posts:", err);
        setPosts([]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <>
      <SEO
        title="Blog"
        description="Read the latest news, guides, and insights about home loans, refinancing, and the Australian property market from Path Financial Solutions."
        url="/blog"
      />

      {/* Hero */}
      <section className="bg-primary text-primary-foreground py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <h1 className="font-serif text-4xl md:text-5xl font-medium mb-4">
              Blog & News
            </h1>
            <p className="text-lg text-primary-foreground/80">
              Stay informed with the latest insights on home loans, market
              trends, and expert financial advice.
            </p>
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-lg text-muted-foreground">
                No blog posts yet. Check back soon!
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <article
                  key={post.id}
                  className="group bg-white rounded-xl border border-border overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {/* Featured Image */}
                  {post.featuredImage ? (
                    <Link to={`/blog/${post.slug}`} className="block">
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={post.featuredImage}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    </Link>
                  ) : (
                    <Link to={`/blog/${post.slug}`} className="block">
                      <div className="aspect-video bg-muted flex items-center justify-center">
                        <span className="text-4xl text-muted-foreground/30">
                          {post.title.charAt(0)}
                        </span>
                      </div>
                    </Link>
                  )}

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                      <span className="flex items-center gap-1">
                        <FiCalendar className="w-4 h-4" />
                        {new Date(post.publishedAt).toLocaleDateString(
                          "en-AU",
                          {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </span>
                    </div>

                    <Link to={`/blog/${post.slug}`}>
                      <h2 className="font-serif text-xl font-medium text-foreground group-hover:text-primary transition-colors mb-2">
                        {post.title}
                      </h2>
                    </Link>

                    {post.excerpt && (
                      <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>
                    )}

                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center text-sm font-medium text-primary hover:underline"
                    >
                      Read more
                      <FiArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
